import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getUserProfileCall } from '@/core/state/api';
import { SurveyTemplate } from '@/surveys';
import {
  getSurveyCampaignByIdResponsesResultsStatsCall,
  getSurveysCampaignsCall,
  getTemplatesCall,
  postEmployerByIdSurveyResultCall,
} from '@/surveys/state/api';
import { UserContext } from '@/swagger/constants/slash-enums';

const NUMBER_OF_SKILL_TO_BOOST = 2;

export const useSkillsToBoost = () => {
  const { email, activeContext } = getUserProfileCall.hook();
  const {
    response: responsePostEmployerByIdSurveyResult,
    success: successPostEmployerByIdSurveyResult,
    execute: executePostEmployerByIdSurveyResult,
  } = postEmployerByIdSurveyResultCall.hook();
  const {
    execute: executeGetSurveyCampaignByIdResponsesResultsStats,
    conElementsOrganizationStats,
  } = getSurveyCampaignByIdResponsesResultsStatsCall.hook();
  const { softSkillTemplate } = getTemplatesCall.hook();
  const { execute: executeGetSurveysCampaigns } = getSurveysCampaignsCall.hook();
  // Tracks the templateId already requested, so the effect fires once per template
  // instead of deriving "already fetched" from the response content.
  const requestedTemplateIdRef = useRef<string | undefined>(undefined);

  const skillsToBoost = useMemo(() => {
    if (activeContext === UserContext.ADMIN) {
      if (!conElementsOrganizationStats || !conElementsOrganizationStats.length) {
        return [];
      }
      const metrics = conElementsOrganizationStats.map(el => el.metric?.name?.toLowerCase() ?? '');
      return metrics.slice(0, NUMBER_OF_SKILL_TO_BOOST);
    }
    if (!responsePostEmployerByIdSurveyResult) {
      return [];
    }

    const softSkillsSurvey = responsePostEmployerByIdSurveyResult.find(
      survey =>
        survey.templateName === SurveyTemplate.SoftSkillAssessment && survey.completed === true,
    );

    if (!softSkillsSurvey?.results?.results) {
      return [];
    }

    // Sort by ascending score and take the first 2 skills
    return [...softSkillsSurvey.results.results]
      .sort((a, b) => (a.score ?? 0) - (b.score ?? 0))
      .slice(0, NUMBER_OF_SKILL_TO_BOOST)
      .map(metric => metric.metric?.name?.toLowerCase() ?? '');
  }, [activeContext, conElementsOrganizationStats, responsePostEmployerByIdSurveyResult]);

  const isSkillToBoost = useCallback(
    (category: string | undefined) => {
      if (!category || skillsToBoost.length === 0) {
        return false;
      }

      const categoryLower = category.toLowerCase();
      return skillsToBoost.some(skill => skill === categoryLower);
    },
    [skillsToBoost],
  );

  useEffect(() => {
    if (activeContext === UserContext.ADMIN) {
      // Execute the call only if the call for that templateId has not been made yet.
      // Guarding on the ref (not the response) avoids an infinite loop when the
      // campaigns response is empty, where surveyTemplateId would always be undefined.
      if (softSkillTemplate.id && requestedTemplateIdRef.current !== softSkillTemplate.id) {
        requestedTemplateIdRef.current = softSkillTemplate.id;
        executeGetSurveysCampaigns({
          templateId: softSkillTemplate.id,
          successSaga: ({ response }) => {
            if (response?.[0]?.id) {
              executeGetSurveyCampaignByIdResponsesResultsStats({
                campaignId: response[0].id,
              });
            }
          },
        });
      }
      return;
    }
    if (!responsePostEmployerByIdSurveyResult && !successPostEmployerByIdSurveyResult && email) {
      executePostEmployerByIdSurveyResult({ username: email });
    }
  }, [
    responsePostEmployerByIdSurveyResult,
    successPostEmployerByIdSurveyResult,
    email,
    executePostEmployerByIdSurveyResult,
    activeContext,
    softSkillTemplate.id,
    executeGetSurveyCampaignByIdResponsesResultsStats,
    softSkillTemplate,
    executeGetSurveysCampaigns,
  ]);

  return {
    skillsToBoost,
    isSkillToBoost,
  };
};
